import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, apiJson, resolvePublicStorageUrl } from '../apiClient';
import { useAuth } from '../AuthContext';
import { tenantDisplayName } from '../utils/businessType';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';
import { brandingFromUser } from '../utils/tenantBranding';
import { canModerateBranch } from '../utils/userLocale';
import './BrickFactoryPage.css';
import './FactoryBrickDamagePage.css';

const DAMAGE_EXPORT_HEAD = [
  'Date',
  'Brick type',
  'Damaged (pcs)',
  'Deducted from',
  'Reason',
  'Status',
  'Recorded by',
  'Approved by',
];

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatPcs(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-TZ');
}

function statusLabel(status) {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  if (status === 'pending') return 'Pending';
  return status || '—';
}

function evidenceUrlFor(batch) {
  return resolvePublicStorageUrl(batch?.evidenceUrl || batch?.imageUrl || null);
}

function isVideoEvidence(batch) {
  if (batch?.evidenceMediaType === 'video') return true;
  if (batch?.evidenceMediaType === 'image') return false;
  const url = evidenceUrlFor(batch);
  if (!url) return false;
  return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
}

function DamageEvidenceModal({ batch, onClose }) {
  if (!batch) return null;

  const mediaUrl = evidenceUrlFor(batch);
  const isVideo = isVideoEvidence(batch);

  return (
    <div className="factory-damage-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="factory-damage-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="factory-damage-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="factory-damage-modal__header">
          <div>
            <h2 id="factory-damage-modal-title">Damage evidence</h2>
            <p className="factory-damage-modal__sub">
              {batch.brickTypeName} · {batch.productionDate}
            </p>
          </div>
          <button type="button" className="factory-damage-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <dl className="factory-damage-modal__meta">
          <div>
            <dt>Damaged</dt>
            <dd>{formatPcs(batch.quantityDamaged ?? batch.quantityWastage)} pcs</dd>
          </div>
          <div>
            <dt>Deducted from</dt>
            <dd>{batch.deductFrom === 'ready' ? 'Ready to sell' : 'Curing'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{statusLabel(batch.approvalStatus)}</dd>
          </div>
          <div>
            <dt>Recorded by</dt>
            <dd>{batch.recordedBy || '—'}</dd>
          </div>
          {batch.approvedBy ? (
            <div>
              <dt>Approved by</dt>
              <dd>{batch.approvedBy}</dd>
            </div>
          ) : null}
        </dl>

        {batch.notes?.trim() ? (
          <p className="factory-damage-modal__reason">
            <strong>Reason:</strong> {batch.notes.trim()}
          </p>
        ) : null}

        <div className="factory-damage-modal__media">
          {mediaUrl ? (
            isVideo ? (
              <video src={mediaUrl} controls playsInline className="factory-damage-modal__video">
                <track kind="captions" />
              </video>
            ) : (
              <img src={mediaUrl} alt="Damage evidence" className="factory-damage-modal__image" />
            )
          ) : (
            <p className="factory-damage-modal__no-media">Hakuna picha au video iliyopakiwa kwa rekodi hii.</p>
          )}
        </div>

        {mediaUrl ? (
          <div className="factory-damage-modal__actions">
            <a
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="brick-btn brick-btn--secondary brick-btn--sm"
            >
              Open in new tab
            </a>
            <button type="button" className="brick-btn brick-btn--secondary brick-btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <div className="factory-damage-modal__actions">
            <button type="button" className="brick-btn brick-btn--secondary brick-btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function damageExportRow(b) {
  const from =
    b.deductFrom === 'ready'
      ? 'Ready to sell'
      : b.deductFrom === 'curing'
        ? 'Curing'
        : b.deductFrom ?? '';
  return [
    b.productionDate ?? '',
    b.brickTypeName ?? '',
    b.quantityDamaged ?? b.quantityWastage ?? '',
    from,
    b.notes?.trim() || '—',
    statusLabel(b.approvalStatus),
    b.recordedBy ?? '',
    b.approvedBy ?? '',
  ];
}

export default function FactoryBrickDamagePage({ onNavigate }) {
  const { user } = useAuth();
  const canModerate = canModerateBranch(user?.role);
  const [types, setTypes] = useState([]);
  const [damageBatches, setDamageBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [evidencePreviewType, setEvidencePreviewType] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [viewBatch, setViewBatch] = useState(null);
  const [form, setForm] = useState({
    brickTypeId: '',
    damageDate: todayKey(),
    quantityDamaged: '',
    deductFrom: 'curing',
    notes: '',
  });

  const activeTypes = useMemo(() => types.filter((t) => t.isActive !== false), [types]);
  const exportBranding = useMemo(() => brandingFromUser(user), [user]);
  const exportRows = useMemo(() => damageBatches.map((b) => damageExportRow(b)), [damageBatches]);
  const pendingBatches = useMemo(
    () => damageBatches.filter((b) => b.approvalStatus === 'pending'),
    [damageBatches]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const [ctx, prod] = await Promise.all([
        apiJson('/brick/production-context'),
        apiJson('/brick/production'),
      ]);
      setTypes(ctx.types || []);
      const all = prod.batches || [];
      setDamageBatches(all.filter((b) => b.recordType === 'damage'));
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Failed to load damaged stock.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!form.brickTypeId && activeTypes.length) {
      setForm((f) => ({ ...f, brickTypeId: String(activeTypes[0].id) }));
    }
  }, [activeTypes, form.brickTypeId]);

  useEffect(() => {
    return () => {
      if (evidencePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(evidencePreview);
      }
    };
  }, [evidencePreview]);

  const resetEvidence = () => {
    if (evidencePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(evidencePreview);
    }
    setEvidenceFile(null);
    setEvidencePreview(null);
    setEvidencePreviewType(null);
    setFileKey((k) => k + 1);
  };

  const handleEvidenceChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (evidencePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(evidencePreview);
    }
    setEvidenceFile(file);
    if (!file) {
      setEvidencePreview(null);
      setEvidencePreviewType(null);
      return;
    }
    const isVideo = file.type.startsWith('video/');
    setEvidencePreviewType(isVideo ? 'video' : 'image');
    if (isVideo) {
      setEvidencePreview(URL.createObjectURL(file));
    } else {
      const reader = new FileReader();
      reader.onloadend = () => setEvidencePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);

    if (evidenceFile) {
      const isVideo = evidenceFile.type.startsWith('video/');
      const isImage = evidenceFile.type.startsWith('image/');
      if (!isVideo && !isImage) {
        setMsg({ type: 'error', text: 'Chagua picha au video tu.' });
        setBusy(false);
        return;
      }
      if (isImage && evidenceFile.size > 2 * 1024 * 1024) {
        setMsg({ type: 'error', text: 'Picha lazima iwe 2MB au chini.' });
        setBusy(false);
        return;
      }
      if (isVideo && evidenceFile.size > 20 * 1024 * 1024) {
        setMsg({ type: 'error', text: 'Video lazima iwe 20MB au chini.' });
        setBusy(false);
        return;
      }
    }

    try {
      const fd = new FormData();
      fd.append('recordType', 'damage');
      fd.append('brickTypeId', String(form.brickTypeId));
      fd.append('productionDate', form.damageDate);
      fd.append('quantityDamaged', String(form.quantityDamaged));
      fd.append('deductFrom', form.deductFrom);
      if (form.notes.trim()) fd.append('notes', form.notes.trim());
      if (evidenceFile) fd.append('evidence', evidenceFile);

      const res = await apiFetch('/brick/production', { method: 'POST', body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Could not record damaged bricks.');
      }
      setForm((f) => ({ ...f, quantityDamaged: '', notes: '' }));
      resetEvidence();
      await load();
      const qty = body.batch?.quantityDamaged ?? Number(form.quantityDamaged);
      setMsg({
        type: 'success',
        text: `Submitted ${qty} damaged pieces for approval. Stock will update after owner/manager approval.`,
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleReview = async (id, approved) => {
    setProcessingId(id);
    setMsg(null);
    try {
      const res = await apiFetch(`/brick/damage/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: approved ? 'approved' : 'rejected' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Could not update damage record.');
      }
      await load();
      setMsg({
        type: 'success',
        text: approved ? 'Damage record approved. Stock updated.' : 'Damage record rejected.',
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Action failed.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDownloadCsv = useCallback(() => {
    if (!damageBatches.length) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const factoryName = tenantDisplayName(user);
    downloadCsv(`damaged-bricks_${stamp}.csv`, [DAMAGE_EXPORT_HEAD, ...exportRows], {
      branding: exportBranding,
      title: `Damaged bricks (${damageBatches.length})`,
      subtitle: `${factoryName} — matofali yaliyoharibika`,
    });
  }, [damageBatches.length, exportRows, exportBranding, user]);

  const handleDownloadPdf = useCallback(async () => {
    if (!damageBatches.length || exporting) return;
    setExporting(true);
    setMsg(null);
    try {
      const factoryName = tenantDisplayName(user);
      await exportTableToPdf(
        `Damaged bricks (${damageBatches.length})`,
        DAMAGE_EXPORT_HEAD,
        exportRows,
        {
          branding: exportBranding,
          theme: 'factory-damage',
          subtitle: `${factoryName} — matofali yaliyoharibika`,
          criteria: [
            `Exported: ${new Date().toLocaleString('en-GB')}`,
            `Records: ${damageBatches.length}`,
          ],
        }
      );
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not create PDF.' });
    } finally {
      setExporting(false);
    }
  }, [damageBatches.length, exporting, exportRows, exportBranding, user]);

  const renderStatus = (status) => {
    const label = statusLabel(status);
    const cls =
      status === 'approved'
        ? 'factory-damage-status factory-damage-status--approved'
        : status === 'rejected'
          ? 'factory-damage-status factory-damage-status--rejected'
          : 'factory-damage-status factory-damage-status--pending';
    return <span className={cls}>{label}</span>;
  };

  return (
    <section className="brick-page factory-damage-page page-bleed">
      <header className="factory-damage__hero">
        <div>
          <h1 className="factory-damage__title">Damaged stock</h1>
          <p className="factory-damage__sub">
            Rekodi matofali yaliyoharibika kutoka stock (curing au tayari kuuza). Rekodi zinasubiri idhini ya
            mmiliki kabla stock haijapunguzwa. Uzalishaji mpya —{' '}
            <button type="button" className="brick-link" onClick={() => onNavigate?.('brick-production')}>
              Production batches
            </button>
            .
          </p>
        </div>
        {damageBatches.length ? (
          <div className="brick-batch-export-actions">
            <button
              type="button"
              className="brick-btn brick-btn--secondary brick-btn--sm"
              onClick={handleDownloadCsv}
              disabled={loading || exporting}
            >
              Download CSV
            </button>
            <button
              type="button"
              className="brick-btn brick-btn--secondary brick-btn--sm brick-batch-export-pdf"
              onClick={() => void handleDownloadPdf()}
              disabled={loading || exporting}
            >
              {exporting ? 'PDF…' : 'Download PDF'}
            </button>
          </div>
        ) : null}
      </header>

      {msg ? <div className={`brick-msg brick-msg--${msg.type}`}>{msg.text}</div> : null}

      <section className="brick-card brick-card--damage brick-card--damage-page">
        {!activeTypes.length && !loading ? (
          <p className="brick-empty">
            Ongeza aina ya tofali kwanza.{' '}
            <button type="button" className="brick-link" onClick={() => onNavigate?.('brick-catalog')}>
              Catalog
            </button>
          </p>
        ) : (
          <form className="brick-form brick-form--wide brick-damage-panel__form" onSubmit={handleSubmit}>
            <div className="brick-form__row">
              <label>
                Brick type
                <select
                  required
                  value={form.brickTypeId}
                  onChange={(e) => setForm((f) => ({ ...f, brickTypeId: e.target.value }))}
                >
                  {activeTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} (ready: {t.stockReady ?? 0}, curing: {t.stockCuring ?? 0})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Date
                <input
                  type="date"
                  required
                  value={form.damageDate}
                  onChange={(e) => setForm((f) => ({ ...f, damageDate: e.target.value }))}
                />
              </label>
            </div>
            <div className="brick-form__row">
              <label>
                Damaged (pieces)
                <input
                  type="number"
                  min="1"
                  required
                  value={form.quantityDamaged}
                  onChange={(e) => setForm((f) => ({ ...f, quantityDamaged: e.target.value }))}
                />
              </label>
              <label>
                Deduct from
                <select
                  value={form.deductFrom}
                  onChange={(e) => setForm((f) => ({ ...f, deductFrom: e.target.value }))}
                >
                  <option value="curing">Curing (water)</option>
                  <option value="ready">Ready to sell</option>
                </select>
              </label>
            </div>
            <label>
              Reason (sababu ya uharibifu)
              <textarea
                rows={2}
                placeholder="e.g. imevunjika wakati wa kubeba, mvua, nk."
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
            <div className="factory-damage-file-wrap">
              <span className="factory-damage-file-label">Picha au video (ushahidi)</span>
              <span className="factory-damage-file-hint">
                Si lazima · Picha hadi 2MB · Video hadi 20MB (MP4, MOV, WebM)
              </span>
              <input
                key={fileKey}
                className="factory-damage-file-input"
                id="factory-damage-file"
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm"
                onChange={handleEvidenceChange}
              />
              <label className="factory-damage-file-btn" htmlFor="factory-damage-file">
                Chagua picha / video
              </label>
              {evidenceFile?.name ? (
                <span className="factory-damage-file-name">
                  {evidenceFile.name}
                  {evidencePreviewType === 'video' ? ' (video)' : ' (picha)'}
                </span>
              ) : null}
              {evidencePreview ? (
                <div className="factory-damage-media-preview">
                  {evidencePreviewType === 'video' ? (
                    <video src={evidencePreview} controls playsInline className="factory-damage-video-preview" />
                  ) : (
                    <img src={evidencePreview} alt="Preview" />
                  )}
                </div>
              ) : null}
            </div>
            <button type="submit" className="brick-btn brick-btn--secondary" disabled={busy || loading}>
              Submit for approval
            </button>
          </form>
        )}
      </section>

      {canModerate && pendingBatches.length ? (
        <section className="brick-card factory-damage-pending-card">
          <h2 className="factory-damage__history-title">
            Pending approval
            <span className="brick-damage-panel__count">
              {pendingBatches.length} record{pendingBatches.length !== 1 ? 's' : ''}
            </span>
          </h2>
          <p className="factory-damage-pending-lead">
            Thibitisha au kataa rekodi hizi kabla stock haijapunguzwa.
          </p>
        </section>
      ) : null}

      <section className="brick-card brick-card--grow">
        <h2 className="factory-damage__history-title">
          History
          <span className="brick-damage-panel__count">
            {damageBatches.length} record{damageBatches.length !== 1 ? 's' : ''}
          </span>
        </h2>
        {loading ? <p className="brick-loading">Loading…</p> : null}
        {!loading && !damageBatches.length ? (
          <p className="brick-empty">Hakuna rekodi za uharibifu bado.</p>
        ) : null}
        {damageBatches.length ? (
          <div className="brick-table-wrap">
            <table className="brick-table brick-table--damage">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Brick type</th>
                  <th>Damaged (pcs)</th>
                  <th>Deducted from</th>
                  <th>Reason</th>
                  <th>Evidence</th>
                  <th>Status</th>
                  <th>Recorded by</th>
                  <th>Approved by</th>
                  {canModerate ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {damageBatches.map((b) => (
                  <tr key={b.id} className={b.approvalStatus === 'pending' ? 'factory-damage-row--pending' : ''}>
                    <td>{b.productionDate}</td>
                    <td>{b.brickTypeName}</td>
                    <td>
                      <strong>{formatPcs(b.quantityDamaged ?? b.quantityWastage)}</strong>
                    </td>
                    <td>
                      <span className="brick-tag brick-tag--damage">
                        {b.deductFrom === 'ready' ? 'Ready to sell' : 'Curing'}
                      </span>
                    </td>
                    <td className="brick-table__notes">{b.notes?.trim() || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="factory-damage-view-btn"
                        onClick={() => setViewBatch(b)}
                      >
                        View
                        {evidenceUrlFor(b) ? (
                          <span className="factory-damage-view-btn__tag">
                            {isVideoEvidence(b) ? 'Video' : 'Photo'}
                          </span>
                        ) : null}
                      </button>
                    </td>
                    <td>{renderStatus(b.approvalStatus)}</td>
                    <td>{b.recordedBy || '—'}</td>
                    <td>{b.approvedBy || '—'}</td>
                    {canModerate ? (
                      <td>
                        {b.approvalStatus === 'pending' ? (
                          <div className="factory-damage-row-actions">
                            <button
                              type="button"
                              className="factory-damage-btn-approve"
                              disabled={processingId === b.id}
                              onClick={() => void handleReview(b.id, true)}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="factory-damage-btn-reject"
                              disabled={processingId === b.id}
                              onClick={() => void handleReview(b.id, false)}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <DamageEvidenceModal batch={viewBatch} onClose={() => setViewBatch(null)} />
    </section>
  );
}
